/**
 * Das Exportfenster der Kreditkarten: DATEV-Buchungsstapel (EXTF) oder, seit
 * 30.09.2026, camt.053 mit fester Ersatz-IBAN je Karte ("Karte wie Bankkonto").
 * Eigenes Fenster neben Exportieren.tsx, das fuer Kontoauszuege bleibt.
 *
 * Zeigt je Karte, was in den Stapel geht und was dem entgegensteht, in
 * Klartext. Gewaehlt werden Karten, nicht einzelne Abrechnungen: eine Karte
 * geht mit allen fertigen Abrechnungen, damit ihr Kartenkonto lueckenlos
 * bleibt. Eine Datei je Karte und Wirtschaftsjahr, mehrere als ZIP.
 */
import { useEffect, useState } from 'react'
import { Link } from 'react-router'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { api, ApiFehler, type Kartenvorschau, type KartenvorschauKarte } from '../lib/api'
import { iban, zeitraum } from '../lib/format'
import { pfad } from '../lib/art'
import { endeKurz, NUR_CAMT } from '../lib/karte'
import { Fenster } from './Fenster'
import { KartenAnleitung } from './KartenAnleitung'
import { herunterladen } from './Exportieren'
import { DatevAnleitung } from './DatevAnleitung'

type Kartenformat = 'datev_extf' | 'camt053'

function hindernisseFuer(g: KartenvorschauKarte, format: Kartenformat): string[] {
  return format === 'camt053' ? g.camtHindernisse : g.hindernisse
}

function exportierbar(g: KartenvorschauKarte, format: Kartenformat): g is KartenvorschauKarte & { karte: NonNullable<KartenvorschauKarte['karte']> } {
  return g.karte !== null && hindernisseFuer(g, format).length === 0 && g.bereit.length > 0
}

function karteTitel(g: KartenvorschauKarte): string {
  if (!g.karte) return g.kennungLesbar
  return [g.karte.anbieter, endeKurz(g.karte.endziffern), g.karte.bezeichnung ? `· ${g.karte.bezeichnung}` : null].filter(Boolean).join(' ')
}

export function KartenExportieren({ mandantId, offen, aufSchliessen }: { mandantId: string; offen: boolean; aufSchliessen: () => void }) {
  const abfragen = useQueryClient()
  const [format, setzeFormat] = useState<Kartenformat>(NUR_CAMT ? 'camt053' : 'datev_extf')
  const [gewaehlt, setzeGewaehlt] = useState<Set<string>>(new Set())
  const [fehler, setzeFehler] = useState<string | null>(null)
  const [laeuft, setzeLaeuft] = useState(false)
  const [fertig, setzeFertig] = useState<{ id: string; dateiname: string; dateien: number } | null>(null)
  const [anleitung, setzeAnleitung] = useState(false)

  const vorschau = useQuery({
    queryKey: ['kartenexportvorschau', mandantId],
    queryFn: () => api.holen<Kartenvorschau>(`/api/mandanten/${mandantId}/kartenexport`),
    enabled: offen,
    staleTime: 0,
  })
  const v = vorschau.data
  const moeglich = (v?.karten ?? []).filter((g) => exportierbar(g, format))

  // Vorausgewaehlt ist jede Karte, die im gewaehlten Format exportiert werden kann.
  useEffect(() => {
    if (v) setzeGewaehlt(new Set(v.karten.filter((g) => exportierbar(g, format)).map((g) => g.karte!.id)))
  }, [v, format])

  const auswahl = moeglich.filter((g) => gewaehlt.has(g.karte.id))
  const abrechnungen = auswahl.reduce((s, g) => s + g.bereit.length, 0)

  function umschalten(id: string) {
    setzeGewaehlt((alt) => {
      const neu = new Set(alt)
      if (neu.has(id)) neu.delete(id)
      else neu.add(id)
      return neu
    })
  }

  function schliessen() {
    setzeFehler(null)
    setzeFertig(null)
    setzeAnleitung(false)
    aufSchliessen()
  }

  async function erzeugen() {
    setzeFehler(null)
    setzeLaeuft(true)
    try {
      const antwort = await api.senden<{ id: string; dateiname: string; dateien: number }>(`/api/mandanten/${mandantId}/kartenexport`, {
        kreditkarteIds: auswahl.map((g) => g.karte.id),
        format,
      })
      setzeFertig(antwort)
      // Erst nachladen, dann herunterladen: Safari bricht laufende Abfragen ab, sobald ein Download anlaeuft.
      await abfragen.invalidateQueries()
      herunterladen(antwort.id)
    } catch (problem) {
      setzeFehler(problem instanceof ApiFehler ? problem.message : format === 'camt053' ? 'Die camt-Datei konnte nicht erzeugt werden.' : 'Der Buchungsstapel konnte nicht erzeugt werden.')
      await abfragen.invalidateQueries({ queryKey: ['kartenexportvorschau', mandantId] })
    } finally {
      setzeLaeuft(false)
    }
  }

  return (
    <Fenster titel={format === 'camt053' ? 'camt.053 für DATEV erzeugen' : 'DATEV-Buchungsstapel erzeugen'} offen={offen} aufSchliessen={schliessen} breit>
      <div className="formular">
        {fehler && <div className="fehler">{fehler}</div>}

        {fertig ? (
          <>
            <div className="bestaetigung">
              Erzeugt: {fertig.dateiname}
              {fertig.dateien > 1 ? `, ${fertig.dateien} Dateien in einem ZIP, ${format === 'camt053' ? 'eine je Abrechnung' : 'eine je Karte und Wirtschaftsjahr'}` : ''}. Der Download startet von selbst.
            </div>
            <button type="button" className="knopf" onClick={() => herunterladen(fertig.id)}>Noch einmal herunterladen</button>
            <p className="leiser">Die Datei liegt ab jetzt unter <Link to={pfad('kreditkarte', '/exporte')} className="verweis" onClick={schliessen}>Exporte</Link> und lässt sich dort jederzeit erneut herunterladen.</p>
          </>
        ) : (
          <>
            {!NUR_CAMT && <div className="exportkonto-kopf" role="radiogroup" aria-label="Format">
              <label className="schalter">
                <input type="radio" name="kartenformat" checked={format === 'datev_extf'} onChange={() => setzeFormat('datev_extf')} />
                <span>Buchungsstapel</span>
              </label>
              <label className="schalter">
                <input type="radio" name="kartenformat" checked={format === 'camt053'} onChange={() => setzeFormat('camt053')} />
                <span>camt.053, Karte wie Bankkonto</span>
              </label>
            </div>}
            {vorschau.isPending && <div className="leiser">Wird zusammengestellt …</div>}
            {vorschau.isError && <div className="fehler">{vorschau.error instanceof Error ? vorschau.error.message : 'Die Vorschau konnte nicht geladen werden.'}</div>}
            {v && v.karten.length === 0 && (
              <div className="leer-klein"><strong>Keine offene Kreditkartenabrechnung.</strong></div>
            )}
            {v && v.karten.length > 0 && (
              <div className="exportkonten">
                {v.karten.map((g) => {
                  const kannMit = exportierbar(g, format)
                  const id = g.karte?.id ?? g.kennung ?? 'ohne'
                  return (
                    <div key={id} className="exportkonto">
                      <div className="exportkonto-kopf">
                        {kannMit ? (
                          <label className="schalter">
                            <input type="checkbox" checked={gewaehlt.has(g.karte.id)} onChange={() => umschalten(g.karte.id)} />
                            <span>{karteTitel(g)}</span>
                          </label>
                        ) : (
                          <span>{karteTitel(g)}</span>
                        )}
                        <span className="leiser">
                          {format === 'camt053'
                            ? g.ersatzIban ? `${iban(g.ersatzIban)}, camt.053.001.${g.camtVersion}${g.camtVersionFestgelegt ? ' wie beim letzten Export' : ''}, ` : ''
                            : g.karte?.datev_konto != null && g.karte.datev_gegenkonto != null ? `Konto ${g.karte.datev_konto} an ${g.karte.datev_gegenkonto}, ` : ''}
                          {g.anzahlBuchungen === 1 ? 'eine Buchung' : `${g.anzahlBuchungen} Buchungen`}
                        </span>
                      </div>
                      {g.bereit.map((a) => (
                        <div key={a.id} className="exportkonto-zeile">
                          <span>{a.bezeichnung}</span>
                          <span className="leiser zahl">{zeitraum(a.zeitraum_von, a.zeitraum_bis)}</span>
                          {a.stand === 'freigegeben' && <span className="marke">freigegeben</span>}
                        </div>
                      ))}
                      {g.nichtBereit.length > 0 && (
                        <div className="exportkonto-zeile leiser">
                          {g.nichtBereit.length === 1 ? 'Eine Abrechnung wartet' : `${g.nichtBereit.length} Abrechnungen warten`} noch auf Prüfung und {g.nichtBereit.length === 1 ? 'geht' : 'gehen'} nicht mit.
                        </div>
                      )}
                      {g.nullbuchungen > 0 && (
                        <div className="exportkonto-zeile leiser">
                          {g.nullbuchungen === 1 ? 'Eine Nullbuchung geht' : `${g.nullbuchungen} Nullbuchungen gehen`} nicht in {format === 'camt053' ? 'die Datei' : 'den Stapel'}, DATEV nimmt keinen Umsatz 0,00.
                        </div>
                      )}
                      {g.bereit.length === 0 && g.nichtBereit.length > 0 && (
                        <div className="exportkonto-zeile text-warnung">Noch keine Abrechnung dieser Karte ist fertig.</div>
                      )}
                      {hindernisseFuer(g, format).map((h) => (
                        <div key={h} className="exportkonto-zeile text-warnung">{h}</div>
                      ))}
                    </div>
                  )
                })}
              </div>
            )}
            {v && format === 'camt053' && v.karten.some((g) => g.ersatzIban) && (
              <div className="hinweis">
                Jede Karte bekommt eine feste Ersatz-IBAN mit der Bankleitzahl 99999999. Sie gehört zu keiner Bank und bleibt jeden Monat gleich.
                In DATEV wird sie beim Mandanten einmal als Bankverbindung zum Kartenkonto angelegt, etwa 1361. Danach liest DATEV die Abrechnung wie einen Kontoauszug ein.
              </div>
            )}
            {v && v.karten.some((g) => hindernisseFuer(g, format).length > 0) && (
              <div className="hinweis">Stammdaten, Karten und Konten stehen in der Akte des Mandanten unter „DATEV-Stammdaten“ und „Kreditkarten“.</div>
            )}
            {abrechnungen > 0 && !NUR_CAMT && (
              <div className="hinweis">
                Nach dem Erzeugen gelten die Abrechnungen als exportiert. DATEV nimmt einen Import nicht zurück, ein zweiter Export für denselben Zeitraum ist gesperrt{NUR_CAMT ? '' : ', auch im anderen Format'}.
              </div>
            )}
          </>
        )}

        <button type="button" className="aufklapper" aria-expanded={anleitung} onClick={() => setzeAnleitung(!anleitung)}>
          {format === 'camt053' ? 'So kommt die Datei in DATEV' : 'So kommt der Buchungsstapel in DATEV'}
        </button>
        {anleitung && (format === 'camt053' ? <DatevAnleitung format="camt053" /> : <KartenAnleitung />)}

        <div className="formular-fuss">
          <button type="button" className="knopf still" onClick={schliessen}>{fertig ? 'Schließen' : 'Abbrechen'}</button>
          {!fertig && (
            <button type="button" className="knopf gefuellt" disabled={laeuft || abrechnungen === 0} onClick={() => void erzeugen()}>
              {laeuft ? 'Wird erzeugt …' : format === 'camt053' ? 'camt.053 erzeugen' : 'Buchungsstapel erzeugen'}
            </button>
          )}
        </div>
      </div>
    </Fenster>
  )
}
